import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { recordAPI, studentAPI, modelsAPI, attachmentAPI, getCategoryInfo } from "../services/api";

const STATUS_BADGE = { draft: "badge-draft", review1: "badge-review1", review2: "badge-review2", final: "badge-final", done: "badge-done" };
const STATUS_LABEL = { draft: "초안", review1: "1차검토 완료", review2: "2차검토 완료", final: "최종 완료", done: "완료" };

function CharCounter({ text, limit }) {
  const charLimit = limit || 500;
  const count = (text || "").length;
  const cls = count > charLimit ? "over" : count >= charLimit * 0.9 ? "warn" : "";
  return <div className={`char-counter ${cls}`}>{count.toLocaleString()} / {charLimit.toLocaleString()}자</div>;
}

export default function RecordEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [student, setStudent] = useState(null);
  const [models, setModels] = useState([]);
  const [activeTab, setActiveTab] = useState("draft");
  const [review1Model, setReview1Model] = useState("");
  const [review2Model, setReview2Model] = useState("");
  const [finalModel, setFinalModel] = useState("");
  const [loading, setLoading] = useState({ r1: false, r2: false, fin: false });
  const [error, setError] = useState("");
  const [draftEdit, setDraftEdit] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [review1Note, setReview1Note] = useState("");
  const [review2Note, setReview2Note] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [{ data }, students, { data: modelList }] = await Promise.all([
      recordAPI.get(id),
      studentAPI.list(),
      modelsAPI.list(),
    ]);
    setRecord(data);
    setDraftEdit(data.draft);
    setReview1Note(data.review1_note || "");
    setReview2Note(data.review2_note || "");
    setStudent(students.data.find((s) => s.id === data.student_id));
    const { data: atts } = await attachmentAPI.list(id);
    setAttachments(atts);
    setModels(modelList);
    if (modelList.length > 0) {
      setReview1Model((v) => v || modelList[0].value);
      setReview2Model((v) => v || modelList[0].value);
      setFinalModel((v) => v || modelList[0].value);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runReview = async (step) => {
    setError("");
    const key = step === 1 ? "r1" : step === 2 ? "r2" : "fin";
    setLoading({ ...loading, [key]: true });
    try {
      let data;
      if (step === 1) ({ data } = await recordAPI.review1(record.id, review1Model));
      else if (step === 2) ({ data } = await recordAPI.review2(record.id, review2Model, review1Note));
      else ({ data } = await recordAPI.final(record.id, finalModel, review2Note));
      setRecord(data);
      setActiveTab(step === 1 ? "review1" : step === 2 ? "review2" : "final");
    } catch (err) {
      setError(err.response?.data?.detail || "AI 호출 중 오류가 발생했습니다");
    } finally {
      setLoading({ ...loading, [key]: false });
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setError("");
    try {
      const { data } = await attachmentAPI.upload(record.id, files);
      setAttachments((prev) => [...prev, ...data]);
    } catch (err) {
      setError(err.response?.data?.detail || "파일 업로드 중 오류가 발생했습니다");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleFileDelete = async (attachmentId) => {
    try {
      await attachmentAPI.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      setError(err.response?.data?.detail || "파일 삭제 중 오류가 발생했습니다");
    }
  };

  const saveDraft = async () => {
    const hasAiResult = record.review1_result || record.review2_result || record.final_result;
    if (hasAiResult) {
      const ok = window.confirm(
        "초안을 수정하면 1차·2차·최종 AI 검토 결과가 모두 삭제됩니다.\n계속하시겠습니까?"
      );
      if (!ok) return;
    }
    await recordAPI.updateDraft(record.id, { student_id: record.student_id, category: record.category, subject: record.subject, draft: draftEdit });
    load();
    setEditMode(false);
  };

  if (!record) return <div style={{ padding: 40, textAlign: "center" }}>불러오는 중...</div>;

  const catInfo = getCategoryInfo(record?.category);

  return (
    <>
      <nav>
        <div className="nav-inner">
          <button onClick={() => navigate("/")}>← 학생 목록</button>
          <h1>생활기록부 편집</h1>
          <div className="nav-right">
            <button onClick={() => navigate(`/student/${student?.id}`)}>생기부 관리 →</button>
          </div>
        </div>
      </nav>

      <div className="container" style={{ marginTop: 24 }}>
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="flex items-center gap-2" style={{ flexWrap: "wrap", gap: 12 }}>
            <div>
              <span className="text-sm text-gray">학생</span>
              <div className="font-bold">{student?.name || "-"} ({student?.grade}학년 {student?.class_num}반 {student?.number}번)</div>
            </div>
            <div>
              <span className="text-sm text-gray">항목</span>
              <div className="font-bold">{catInfo.label}{record.subject ? ` — ${record.subject}` : ""}</div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <span className={`badge ${STATUS_BADGE[record.status]}`}>{STATUS_LABEL[record.status]}</span>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* 결과 탭 */}
        <div className="tabs">
          <button className={`tab ${activeTab === "draft" ? "active" : ""}`} onClick={() => setActiveTab("draft")}>초안</button>
          <button className={`tab ${activeTab === "review1" ? "active" : ""}`} onClick={() => setActiveTab("review1")} disabled={!record.review1_result}>1차 검토</button>
          <button className={`tab ${activeTab === "review2" ? "active" : ""}`} onClick={() => setActiveTab("review2")} disabled={!record.review2_result}>2차 검토</button>
          <button className={`tab ${activeTab === "final" ? "active" : ""}`} onClick={() => setActiveTab("final")} disabled={!record.final_result}>최종 결과</button>
        </div>

        {activeTab === "draft" && (
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.status !== "draft" ? "done" : ""}`}>초</div>
              <h3 className="font-bold">초안</h3>
              {!editMode && <button className="btn btn-outline" style={{ padding: "4px 12px", fontSize: ".82rem", marginLeft: "auto" }} onClick={() => setEditMode(true)}>수정</button>}
            </div>
            {editMode ? (
              <>
                <textarea className="editor-area" value={draftEdit} onChange={(e) => setDraftEdit(e.target.value)} />
                <CharCounter text={draftEdit} limit={catInfo.charLimit} />
                <div className="flex mt-2" style={{ gap: 8 }}>
                  <button className="btn btn-primary" onClick={saveDraft}>저장 (검토 초기화)</button>
                  <button className="btn btn-outline" onClick={() => setEditMode(false)}>취소</button>
                </div>
              </>
            ) : (
              <>
                <div className="result-box">{record.draft}</div>
                <CharCounter text={record.draft} limit={catInfo.charLimit} />
              </>
            )}
          </div>
        )}

        {activeTab === "review1" && (
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.review1_result ? "done" : ""}`}>1</div>
              <h3 className="font-bold">1차 검토 결과</h3>
              {record.review1_model && <span className="text-sm text-gray">({record.review1_model})</span>}
            </div>
            <div className="result-box">{record.review1_result || <span className="text-gray">아직 1차 검토가 진행되지 않았습니다</span>}</div>
            {record.review1_result && <CharCounter text={record.review1_result} limit={catInfo.charLimit} />}
          </div>
        )}

        {activeTab === "review2" && (
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.review2_result ? "done" : ""}`}>2</div>
              <h3 className="font-bold">2차 검토 결과</h3>
              {record.review2_model && <span className="text-sm text-gray">({record.review2_model})</span>}
            </div>
            {record.review1_note && (
              <div style={{ marginBottom: 10 }}>
                <div className="text-sm text-gray" style={{ marginBottom: 4 }}>교사 추가 지시사항</div>
                <div className="note-box">{record.review1_note}</div>
              </div>
            )}
            <div className="result-box">{record.review2_result || <span className="text-gray">아직 2차 검토가 진행되지 않았습니다</span>}</div>
            {record.review2_result && <CharCounter text={record.review2_result} limit={catInfo.charLimit} />}
          </div>
        )}

        {activeTab === "final" && (
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.final_result ? "done" : ""}`}>완</div>
              <h3 className="font-bold">최종 결과</h3>
              {record.final_model && <span className="text-sm text-gray">({record.final_model})</span>}
            </div>
            {record.review2_note && (
              <div style={{ marginBottom: 10 }}>
                <div className="text-sm text-gray" style={{ marginBottom: 4 }}>교사 추가 지시사항</div>
                <div className="note-box">{record.review2_note}</div>
              </div>
            )}
            <div className="result-box">{record.final_result || <span className="text-gray">아직 최종 출력이 생성되지 않았습니다</span>}</div>
            {record.final_result && <CharCounter text={record.final_result} limit={catInfo.charLimit} />}
          </div>
        )}

        {/* 참고 자료 업로드 */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="step-header" style={{ marginBottom: 12 }}>
            <h3 className="font-bold">참고 자료 업로드</h3>
            <span className="text-sm text-gray" style={{ marginLeft: 8 }}>AI 생성 시 자동으로 참고합니다 · 최대 5개 · 10MB 이하</span>
          </div>
          <div className="alert alert-info" style={{ marginBottom: 12, padding: "8px 12px", fontSize: ".85rem" }}>
            지원 형식: PDF, DOCX, TXT, XLSX, JPG, PNG · HWP는 PDF로 변환 후 업로드하세요
          </div>
          {attachments.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {attachments.map((a) => (
                <span key={a.id} className="file-chip">
                  📎 {a.original_filename}
                  <button className="file-chip-del" onClick={() => handleFileDelete(a.id)} title="삭제">×</button>
                </span>
              ))}
            </div>
          )}
          {attachments.length < 5 && (
            <label className="upload-zone">
              <input type="file" multiple accept=".pdf,.docx,.txt,.xlsx,.jpg,.jpeg,.png" onChange={handleFileUpload} />
              {uploading
                ? <><span className="spinner" style={{ borderTopColor: "#1a56db", borderColor: "#c7d2fe" }} />업로드 중...</>
                : <span className="text-gray">클릭하여 파일 선택 (여러 파일 동시 선택 가능)</span>
              }
            </label>
          )}
        </div>

        {/* 파이프라인 컨트롤 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* 1차 검토 */}
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.review1_result ? "done" : ""}`}>1</div>
              <h3 className="font-bold">1차 검토</h3>
            </div>
            <div className="ai-row">
              <div className="form-group">
                <label>AI 모델 선택</label>
                <select className="form-control" value={review1Model} onChange={(e) => setReview1Model(e.target.value)}>
                  {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
                </select>
              </div>
              <button className="btn btn-primary" onClick={() => runReview(1)} disabled={loading.r1 || !record.draft?.trim()} style={{ flexShrink: 0, marginBottom: 16 }}>
                {loading.r1 ? <><span className="spinner" />1차 검토 중...</> : record.review1_result ? "재실행" : "1차 검토 시작"}
              </button>
              {!record.draft?.trim() && <span className="text-sm text-gray" style={{ marginBottom: 16 }}>초안을 먼저 작성해주세요</span>}
            </div>
          </div>

          {/* 2차 검토 */}
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.review2_result ? "done" : ""}`}>2</div>
              <h3 className="font-bold">2차 검토</h3>
              {!record.review1_result && <span className="text-sm text-gray" style={{ marginLeft: 8 }}>1차 검토 완료 후 활성화</span>}
            </div>
            {record.review1_result && (
              <div className="form-group">
                <label style={{ color: "#92400e" }}>교사 추가 지시사항 <span className="text-sm text-gray">(선택사항 — 학생 특성·방향성 등)</span></label>
                <textarea
                  className="note-area"
                  value={review1Note}
                  onChange={(e) => setReview1Note(e.target.value)}
                  placeholder="예) 이 학생의 수학적 역량을 더 강조해주세요. 진로를 이공계 방향으로 서술해주세요."
                  disabled={!record.review1_result}
                />
              </div>
            )}
            <div className="ai-row">
              <div className="form-group">
                <label>AI 모델 선택</label>
                <select className="form-control" value={review2Model} onChange={(e) => setReview2Model(e.target.value)} disabled={!record.review1_result}>
                  {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
                </select>
              </div>
              <button className="btn btn-success" onClick={() => runReview(2)} disabled={loading.r2 || !record.review1_result} style={{ flexShrink: 0, marginBottom: 16 }}>
                {loading.r2 ? <><span className="spinner" />2차 검토 중...</> : record.review2_result ? "재실행" : "2차 검토 시작"}
              </button>
            </div>
          </div>

          {/* 최종 출력 */}
          <div className="card">
            <div className="step-header">
              <div className={`step-num ${record.final_result ? "done" : ""}`}>완</div>
              <h3 className="font-bold">최종 출력</h3>
              {!record.review2_result && <span className="text-sm text-gray" style={{ marginLeft: 8 }}>2차 검토 완료 후 활성화</span>}
            </div>
            {record.review2_result && (
              <div className="form-group">
                <label style={{ color: "#92400e" }}>교사 추가 지시사항 <span className="text-sm text-gray">(선택사항 — 최종본 방향성 등)</span></label>
                <textarea
                  className="note-area"
                  value={review2Note}
                  onChange={(e) => setReview2Note(e.target.value)}
                  placeholder="예) 좀 더 구체적인 활동 사례를 넣어주세요. 행동 변화 중심으로 서술해주세요."
                  disabled={!record.review2_result}
                />
              </div>
            )}
            <div className="ai-row">
              <div className="form-group">
                <label>AI 모델 선택</label>
                <select className="form-control" value={finalModel} onChange={(e) => setFinalModel(e.target.value)} disabled={!record.review2_result}>
                  {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
                </select>
              </div>
              <button className="btn btn-success" onClick={() => runReview(3)} disabled={loading.fin || !record.review2_result} style={{ flexShrink: 0, marginBottom: 16 }}>
                {loading.fin ? <><span className="spinner" />최종 생성 중...</> : record.final_result ? "재생성" : "최종 출력 생성"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
